import useModuleFilters from '../../../shared/data/useModuleFilters';
import FilterBar from '../../../shared/components/patterns/FilterBar';
import { tenantFormSchema } from '../adminFilter.schemas';
import IconActions from '../../../shared/components/IconActions';
import DataTable from '../../../shared/components/patterns/DataTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { Eye, Plus, Power, RefreshCw, Trash2, UserRoundCog, Users } from '../../../shared/assets/AppIcons';
import { getRoutePath, ROUTE_IDS } from '@/app/router/route-registry';
import {
  deleteAdminTenant,
  listAdminTenants,
  updateAdminTenantStatus,
} from '@/modules/admin/services/admin-tenants.service';
import { useSession } from '@/app/providers/session-provider';
import { PageActionRow } from '../../../shared/components/patterns';
function tenantDetailPath(tenantId) {
  const routeId = ROUTE_IDS.ADMIN_CONTROL_PLANE_CLIENT_DETAIL;
  const pathTemplate = (routeId ? getRoutePath(routeId) : null) || '/admin/clients/:tenantId';
  return pathTemplate.replace(':tenantId', tenantId);
}
function getStatusBadgeClass(active) {
  return active ? 'control-plane-badge is-success' : 'control-plane-badge is-danger';
}
function getProvisioningBadgeClass(status) {
  if (status === 'ACTIVE') {
    return 'control-plane-badge is-success';
  }
  if (status === 'PROVISIONING' || status === 'PENDING') {
    return 'control-plane-badge is-warning';
  }
  if (status) {
    return 'control-plane-badge is-danger';
  }
  return 'control-plane-badge';
}
function getMigrationBadgeClass(status) {
  if (status === 'NOT_APPLICABLE' || status === 'CUTOVER_COMPLETED') {
    return 'control-plane-badge is-success';
  }
  if (
    status === 'DRY_RUN_READY' ||
    status === 'MIGRATING' ||
    status === 'MIGRATED' ||
    status === 'NOT_STARTED'
  ) {
    return 'control-plane-badge is-warning';
  }
  if (status) {
    return 'control-plane-badge is-danger';
  }
  return 'control-plane-badge';
}
function formatDatabaseStatus(status) {
  if (status === 'ACTIVE') {
    return 'Pronto';
  }
  if (status === 'PROVISIONING') {
    return 'Provisioning';
  }
  if (status === 'PROVISIONING_FAILED') {
    return 'Failed';
  }
  return status || 'Non disponibile';
}
function formatLegacyMigrationStatus(status) {
  if (status === 'NOT_APPLICABLE') {
    return 'Non applicabile';
  }
  if (status === 'NOT_STARTED') {
    return 'Non avviata';
  }
  if (status === 'DRY_RUN_READY') {
    return 'Dry run pronto';
  }
  if (status === 'MIGRATING') {
    return 'Migrazione in corso';
  }
  if (status === 'MIGRATED') {
    return 'Migrata';
  }
  if (status === 'CUTOVER_COMPLETED') {
    return 'Cutover completato';
  }
  if (status === 'MIGRATION_FAILED') {
    return 'Failed';
  }
  return status || 'Non disponibile';
}
function isTenantOperationInProgress(tenant) {

  return (
    tenant?.provisioningStatus === 'PROVISIONING' ||
    tenant?.migrationCutoverPending === true ||
    tenant?.migrationStatus === 'DRY_RUN_READY' ||
    tenant?.migrationStatus === 'MIGRATING' ||
    tenant?.migrationStatus === 'MIGRATED'
  );
}
export default function AdminTenantsPage() {
  const navigate = useNavigate();
  const { setTenantCount, refreshAdminMetrics } = useOutletContext() || {};
  const { enterWorkspaceAccess, canEnterWorkspaceAccess } = useSession();
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingTenantId, setPendingTenantId] = useState('');
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteConfirmationValue, setDeleteConfirmationValue] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const createTenantPath = getRoutePath(ROUTE_IDS.ADMIN_TENANTS_CREATE) || '/admin/tenants/new';
  const refreshTenants = useCallback(
    async ({ loading = false } = {}) => {
      if (loading) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      const result = await listAdminTenants();
      if (!result.ok) {
        setError(result.message);
        setTenants([]);
        if (typeof setTenantCount === 'function') {
          setTenantCount(null);
        }
        setIsLoading(false);
        setIsRefreshing(false);
        return result;
      }
      setTenants(result.data.tenants);
      setError('');
      setLastUpdatedAt(new Date());
      if (typeof setTenantCount === 'function') {
        setTenantCount(result.data.tenants.filter((tenant) => !tenant.isPlatformRoot).length);
      }
      setIsLoading(false);
      setIsRefreshing(false);
      return result;
    },
    [setTenantCount]
  );
  useEffect(() => {
    let active = true;
    async function loadTenants() {
      const result = await refreshTenants({
        loading: true,
      });
      if (!active) {
        return;
      }
      if (!result.ok) {
        return;
      }
    }
    loadTenants();
    return () => {
      active = false;
    };
  }, [refreshTenants]);
  const metrics = useMemo(() => {
    const nonPlatformTenants = tenants.filter((tenant) => !tenant.isPlatformRoot);
    return {
      total: nonPlatformTenants.length,
      active: nonPlatformTenants.filter((tenant) => tenant.active).length,
      inactive: nonPlatformTenants.filter((tenant) => !tenant.active).length,
      totalUsers: nonPlatformTenants.reduce(
        (sum, tenant) => sum + (Number.isFinite(tenant.userCount) ? tenant.userCount : 0),
        0
      ),
    };
  }, [tenants]);
  async function handleToggleTenant(tenant) {
    setPendingTenantId(tenant.id);
    setError('');
    setSuccessMessage('');
    const result = await updateAdminTenantStatus(tenant.id, !tenant.active);
    setPendingTenantId('');
    if (!result.ok) {
      setError(result.message);
      return;
    }
    await refreshTenants();
    setSuccessMessage(result.message || 'Stato tenant aggiornato.');
  }
  async function handleEnterWorkspace(tenant) {
    setPendingTenantId(tenant.id);
    setError('');
    setSuccessMessage('');
    try {
      await enterWorkspaceAccess(tenant.id);
      if (typeof refreshAdminMetrics === 'function') {
        await refreshAdminMetrics();
      }
      navigate('/', {
        replace: true,
      });
    } catch (enterError) {
      setError(enterError.message || 'Accesso workspace non riuscito.');
    } finally {
      setPendingTenantId('');
    }
  }
  function openDeleteDialog(tenant) {
    if (tenant.isPlatformRoot) {
      setError('Il tenant platform-root non puo essere eliminato.');
      return;
    }
    if (tenant.active) {
      setError('Disattiva il tenant prima di eliminarlo.');
      return;
    }
    if (isTenantOperationInProgress(tenant)) {
      setError(
        "Il tenant ha un provisioning o una migrazione in corso. Riprova quando l'operazione e conclusa."
      );
      return;
    }
    setDeleteCandidate(tenant);
    setDeleteConfirmationValue('');
    setError('');
    setSuccessMessage('');
  }
  function closeDeleteDialog() {
    setDeleteCandidate(null);
    setDeleteConfirmationValue('');
  }
  async function handleDeleteTenant() {
    if (!deleteCandidate) {
      return;
    }
    setPendingTenantId(deleteCandidate.id);
    setError('');
    setSuccessMessage('');
    const result = await deleteAdminTenant(deleteCandidate.id);
    setPendingTenantId('');
    if (!result.ok) {
      setError(result.message);
      return;
    }
    const refreshResult = await refreshTenants();
    if (!refreshResult.ok) {
      setError(refreshResult.message);
      return;
    }
    setTenants(refreshResult.data.tenants);
    closeDeleteDialog();
    setSuccessMessage(result.message || 'Tenant eliminato.');
  }
  function handleOpenTenantUsers(tenant) {
    const usersPathTemplate = getRoutePath(ROUTE_IDS.ADMIN_TENANT_USERS) || '/admin/tenants/:tenantId/users';
    navigate(usersPathTemplate.replace(':tenantId', tenant.id));
  }
  function formatLastUpdated(value) {
    if (!value) {
      return '—';
    }
    return value.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  function renderModuleChips(enabledModuleKeys) {
    if (!enabledModuleKeys.length) {
      return <span className="control-plane-table__muted">—</span>;
    }
    const visibleModules = enabledModuleKeys.slice(0, 4);
    const hiddenCount = enabledModuleKeys.length - visibleModules.length;
    const title = enabledModuleKeys.join(', ');
    return (
      <div className="control-plane-module-list is-compact" title={title} aria-label={`Moduli: ${title}`}>
        {visibleModules.map((moduleKey) => (
          <span key={moduleKey} className="control-plane-badge">
            {moduleKey}
          </span>
        ))}
        {hiddenCount > 0 ? <span className="control-plane-badge is-info">+{hiddenCount}</span> : null}
      </div>
    );
  }
  const moduleFilters = useModuleFilters({ schema: tenantFormSchema, rows: tenants });
  const filteredRows = moduleFilters.filterRows(tenants);

  return (
    <section
      className="control-plane-page control-plane-page--floating-actions"
      data-testid="admin-tenants-page"
    >
      <header className="control-plane-topbar">
        <div>
          <div className="control-plane-topbar__title">Tenant management</div>
          <div className="control-plane-topbar__subtitle">Catalogo tenant reale da backend auth</div>
        </div>
        <div className="control-plane-topbar__actions">
          <button
            type="button"
            className="control-plane-button"
            onClick={() => refreshTenants()}
            disabled={isLoading || isRefreshing}
          >
            <RefreshCw size={16} aria-hidden="true" />
            Aggiorna
          </button>
        </div>
      </header>

      <PageActionRow label="Azioni tenant">
        <Link className="wt-primary-action-button" to={createTenantPath} aria-label="Crea tenant">
          <Plus size={16} />
          <span>Crea tenant</span>
        </Link>
      </PageActionRow>

      <div className="control-plane-content">
        <section className="control-plane-metrics control-plane-metrics--3">
          <article className="control-plane-metric">
            <div className="control-plane-metric__label">Tenant attivi</div>
            <div className="control-plane-metric__value">{metrics.active}</div>
            <div className="control-plane-metric__support">Tenant operativi con accesso workspace</div>
          </article>
          <article className="control-plane-metric">
            <div className="control-plane-metric__label">Tenant disattivi</div>
            <div className="control-plane-metric__value">{metrics.inactive}</div>
            <div className="control-plane-metric__support">Tenant sospesi o da riattivare</div>
          </article>
          <article className="control-plane-metric">
            <div className="control-plane-metric__label">
              {metrics.totalUsers > 0 ? 'Totale utenti' : 'Totale tenant'}
            </div>
            <div className="control-plane-metric__value">
              {metrics.totalUsers > 0 ? metrics.totalUsers : metrics.total}
            </div>
            <div className="control-plane-metric__support">
              {metrics.totalUsers > 0
                ? 'Somma utenti reali esposti dal backend'
                : 'Tenant reali non-platform'}
            </div>
          </article>
        </section>
        <FilterBar model={moduleFilters} />


        {isLoading ? <div className="control-plane-empty-state">Caricamento tenant in corso...</div> : null}
        {!isLoading && error ? <div role="alert" className="control-plane-inline-error wt-inline-error">{error}</div> : null}
        {!isLoading && successMessage ? (
          <div className="control-plane-inline-success">{successMessage}</div>
        ) : null}

        {!isLoading && !error ? (
          <section className="control-plane-card">
            <div className="control-plane-card__header">
              <div>
                <div className="control-plane-card__title">Tenant management</div>
                <div className="control-plane-card__subtitle">
                  Stato, provisioning, database e owner invite del catalogo control plane.
                </div>
                <div className="control-plane-card__subtitle">
                  Ultimo aggiornamento: {formatLastUpdated(lastUpdatedAt)}
                </div>
              </div>
            </div>

            {tenants.length === 0 ? (
              <div className="control-plane-empty-state">Nessun tenant disponibile.</div>
            ) : (
              <div className="control-plane-registry">
                <div className="control-plane-registry__content">
                  <DataTable
                    ariaLabel="Workspace"
                    rows={filteredRows}
                    columns={[
                      {
                        id: 'name',
                        header: 'Tenant',
                        render: (tenant) => {
                          return (
                            <>
                              <div className="control-plane-table__primary">{tenant.name}</div>
                              <div className="control-plane-table__meta control-plane-mono" title={tenant.id}>
                                {tenant.id}
                              </div>
                              <div
                                className="control-plane-table__meta control-plane-mono"
                                title={tenant.slug || ''}
                              >
                                Slug: {tenant.slug || '—'}
                              </div>
                            </>
                          );
                        },
                      },
                      {
                        id: 'plan',
                        header: 'Piano',
                        render: (tenant) => {
                          return (
                            <>
                              <div className="control-plane-table__primary">
                                {tenant.planCode || 'standard'}
                              </div>
                            </>
                          );
                        },
                      },
                      {
                        id: 'modules',
                        header: 'Moduli',
                        render: (tenant) => {
                          const enabledModuleKeys = Array.isArray(tenant.enabledModuleKeys)
                            ? tenant.enabledModuleKeys
                            : [];
                          return <>{renderModuleChips(enabledModuleKeys)}</>;
                        },
                      },
                      {
                        id: 'owner',
                        header: 'Owner / Invito',
                        render: (tenant) => {
                          return (
                            <>
                              <div className="control-plane-table__primary">{tenant.ownerEmail || '—'}</div>
                              {tenant.ownerInviteStatus ? (
                                <div
                                  className="control-plane-badge-row"
                                  style={{
                                    marginTop: '6px',
                                  }}
                                >
                                  <span
                                    className={
                                      tenant.ownerInviteStatus === 'ACCEPTED'
                                        ? 'control-plane-badge is-success'
                                        : 'control-plane-badge is-warning'
                                    }
                                  >
                                    {tenant.ownerInviteStatus === 'ACCEPTED'
                                      ? 'Invite accepted'
                                      : 'Invite pending'}
                                  </span>
                                </div>
                              ) : null}
                              <div className="control-plane-table__meta">
                                Utenti: {Number.isFinite(tenant.userCount) ? tenant.userCount : '—'}
                              </div>
                            </>
                          );
                        },
                      },
                      {
                        id: 'active',
                        header: 'Stato',
                        render: (tenant) => {
                          return (
                            <>
                              <span className={getStatusBadgeClass(tenant.active)}>
                                {tenant.active ? 'Attivo' : 'Disattivo'}
                              </span>
                            </>
                          );
                        },
                      },
                      {
                        id: 'provisioning',
                        header: 'Provisioning',
                        render: (tenant) => {
                          return (
                            <>
                              <div className="control-plane-badge-row">
                                <span className={getProvisioningBadgeClass(tenant.provisioningStatus)}>
                                  {formatDatabaseStatus(tenant.provisioningStatus)}
                                </span>
                                <span className={getMigrationBadgeClass(tenant.migrationStatus)}>
                                  {formatLegacyMigrationStatus(tenant.migrationStatus)}
                                </span>
                              </div>
                              {tenant.migrationError ? (
                                <div className="control-plane-table__meta">{tenant.migrationError}</div>
                              ) : null}
                            </>
                          );
                        },
                      },
                      {
                        id: 'database',
                        header: 'DB',
                        render: (tenant) => {
                          return (
                            <>
                              <div
                                className="control-plane-table__primary control-plane-ellipsis control-plane-mono"
                                title={tenant.databaseName || ''}
                              >
                                {tenant.databaseName || '—'}
                              </div>
                              <div
                                className="control-plane-table__meta control-plane-ellipsis"
                                title={
                                  tenant.databaseHost && tenant.databasePort
                                    ? `${tenant.databaseHost}:${tenant.databasePort}`
                                    : ''
                                }
                              >
                                {tenant.databaseHost && tenant.databasePort
                                  ? `${tenant.databaseHost}:${tenant.databasePort}`
                                  : 'dato non disponibile'}
                              </div>
                              {tenant.provisioningError ? (
                                <div className="control-plane-table__meta">{tenant.provisioningError}</div>
                              ) : null}
                            </>
                          );
                        },
                      },
                      {
                        id: 'actions',
                        header: 'Azioni',
                        render: (tenant) => {
                          const isPending = pendingTenantId === tenant.id;
                          const canToggle = !tenant.isPlatformRoot;
                          const isProvisioned = tenant.provisioningStatus === 'ACTIVE';
                          const canEnterWorkspace =
                            canEnterWorkspaceAccess &&
                            tenant.active &&
                            !tenant.isPlatformRoot &&
                            isProvisioned;
                          const canManageUsers = !tenant.isPlatformRoot;
                          const hasOperationInProgress = isTenantOperationInProgress(tenant);
                          const canDelete =
                            !tenant.isPlatformRoot && !tenant.active && !hasOperationInProgress;
                          return (
                            <>
                              <div className="control-plane-action-buttons is-toolbar">
                                <Link
                                  className="control-plane-action-button"
                                  to={tenantDetailPath(tenant.id)}
                                  title="Apri dettaglio cliente"
                                  aria-label={`Apri dettaglio cliente ${tenant.name}`}
                                >
                                  <Eye size={16} aria-hidden="true" />
                                  <span>Cliente</span>
                                </Link>
                                <button
                                  type="button"
                                  className="control-plane-action-button"
                                  onClick={() => handleEnterWorkspace(tenant)}
                                  disabled={!canEnterWorkspace || isPending}
                                  aria-label={`Entra nel workspace ${tenant.name}`}
                                  title={
                                    canEnterWorkspace
                                      ? 'Entra nel workspace'
                                      : 'Accesso disponibile solo per tenant attivi e provisioning completato.'
                                  }
                                >
                                  <Power size={16} aria-hidden="true" />
                                  <span>Entra</span>
                                </button>
                                <button
                                  type="button"
                                  className="control-plane-action-button"
                                  onClick={() => handleOpenTenantUsers(tenant)}
                                  disabled={!canManageUsers || isPending}
                                  title="Gestisci utenti"
                                  aria-label={`Gestisci utenti ${tenant.name}`}
                                >
                                  <Users size={16} aria-hidden="true" />
                                  <span>Utenti</span>
                                </button>
                                <button
                                  type="button"
                                  className="control-plane-action-button"
                                  onClick={() => handleToggleTenant(tenant)}
                                  disabled={!canToggle || isPending}
                                  title={tenant.active ? 'Disattiva tenant' : 'Attiva tenant'}
                                  aria-label={`${tenant.active ? 'Disattiva' : 'Attiva'} ${tenant.name}`}
                                >
                                  <UserRoundCog size={16} aria-hidden="true" />
                                  <span>{tenant.active ? 'Disattiva' : 'Attiva'}</span>
                                </button>
                                <button
                                  type="button"
                                  className="control-plane-action-button is-danger"
                                  onClick={() => openDeleteDialog(tenant)}
                                  disabled={!canDelete || isPending}
                                  aria-label={`Elimina tenant ${tenant.name}`}
                                  title={
                                    tenant.isPlatformRoot
                                      ? 'platform-root non eliminabile.'
                                      : tenant.active
                                        ? 'Disattiva il tenant prima di eliminarlo.'
                                        : hasOperationInProgress
                                          ? 'Il tenant ha un provisioning o una migrazione in corso.'
                                          : ''
                                  }
                                >
                                  <Trash2 size={16} aria-hidden="true" />
                                  <span>Elimina</span>
                                </button>
                              </div>
                              {tenant.isPlatformRoot ? (
                                <div className="control-plane-table__meta">platform-root protetto</div>
                              ) : hasOperationInProgress ? (
                                <div className="control-plane-table__meta">
                                  Operazione tenant in corso: eliminazione bloccata
                                </div>
                              ) : null}
                            </>
                          );
                        },
                      },
                    ]}
                  />
                </div>
              </div>
            )}
          </section>
        ) : null}
      </div>

      {deleteCandidate ? (
        <div className="control-plane-modal-backdrop" role="dialog" aria-modal="true">
          <div className="control-plane-modal">
            <div className="control-plane-modal__title">Conferma eliminazione tenant</div>
            <div className="control-plane-modal__body">
              Digita <strong>{deleteCandidate.id}</strong> per confermare l'eliminazione. I tenant attivi
              devono essere prima disattivati.
            </div>
            <div
              style={{
                marginTop: '14px',
              }}
            >
              <label className="control-plane-field">
                <span>Conferma tenant ID</span>
                <input
                  value={deleteConfirmationValue}
                  onChange={(event) => setDeleteConfirmationValue(event.target.value)}
                  placeholder={deleteCandidate.id}
                />
              </label>
            </div>
            <div className="control-plane-modal__actions"><IconActions>
              <button type="button" className="control-plane-button" onClick={closeDeleteDialog}>
                Annulla
              </button>
              <button
                type="button"
                className="control-plane-button is-primary"
                onClick={handleDeleteTenant}
                disabled={
                  deleteConfirmationValue !== deleteCandidate.id || pendingTenantId === deleteCandidate.id
                }
              >
                Conferma elimina
              </button>
            </IconActions></div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
